import { Component, Host, Prop, h, State } from '@stencil/core';
import Player from '@vimeo/player';
import { AspectRatio } from '../../interfaces/shared/media/aspect-ratio';
import { track } from '../../utils/tracking-utils';
import { setCookie } from '../../services/cookies/cookies';
import { TrackingProps } from '../../interfaces/tracking/tracking';
import { TrackingLocationEnum } from '../../interfaces/tracking/tracking-location-enum';
import { VideoTrackingEventNames } from '../../interfaces/tracking/video/tracking-enum';

@Component({
	tag: 'dng-video',
})
export class Video {
	@Prop() aspectRatio: string;
	@Prop() autoplay: boolean = false;
	@Prop() byline: boolean = false;
	@Prop() displayControls: boolean = false;
	@Prop() colorControls: string = 'FFDB41';
	@Prop() loop: boolean = false;
	@Prop() muted: boolean = false;
	@Prop() player: any;
	@Prop() responsive: boolean = true;
	@Prop() url: string;
	@Prop() width: number;
	@Prop() isHelperVideo: boolean;
	@Prop() mobileWindowThreshold: number = 576;
	@Prop() trackingProps: TrackingProps;

	//Side block
	@Prop() showSideBlock: boolean;
	@Prop() sideBlockBody: string;
	@Prop() sideBlockStart: string;
	@Prop() sideBlockSkip: string;

	// Cookies
	@Prop() cookieVideoKey: string;
	@Prop() cookieVideoValue: boolean = false;
	@Prop() cookieVideoExpirationDays: number;

	@State() windowInnerWidth: number = window.innerWidth;

	private playerElement: HTMLElement;
	private videoContainer: HTMLElement;

	componentDidLoad() {
		this.createPlayer();
		this.isHelperVideo && document.body.classList.add('overlay');
		this.setTrackingProps();
	}

	changePlayIcon() {
		const playBtn = this.playerElement.querySelector('.video__btn-play');
		const playIcon = this.playerElement.querySelector('.video__icon-play');
		playBtn.classList.toggle('play--hide');
		playIcon.classList.toggle('pause');
	}

	createPlayer() {
		this.player = new Player(this.playerElement, {
			autoplay: this.autoplay,
			byline: this.byline,
			color: this.colorControls,
			controls: this.displayControls,
			loop: this.loop,
			muted: this.muted,
			responsive: this.responsive,
			url: this.url,
		});
		this.player.on('ended', () => {
			this.trackEvent(VideoTrackingEventNames.VideoPlaybackEnded);
			this.closePlayer();
		});
		return this.player;
	}

	getAspectRatio() {
		switch (this.aspectRatio) {
			case AspectRatio.Portrait:
				return 'aspect-ratio--portrait';
			case AspectRatio.Square:
				return 'aspect-ratio--square';
			case AspectRatio.Wide:
				return 'aspect-ratio--wide';
		}
	}

	closePlayer() {
		this.player.pause();
		this.videoContainer.classList.add('hide');
		this.cookieVideoValue = true;
		this.trackEvent(VideoTrackingEventNames.VideoPlayerClosed);
		setCookie(
			this.cookieVideoKey,
			this.cookieVideoValue.toString(),
			this.cookieVideoExpirationDays
		);
		document.body.classList.remove('overlay');
	}

	handlePlayer(startPlayer: boolean = false) {
		if (startPlayer) {
			this.player.play();
			this.trackEvent(VideoTrackingEventNames.VideoPlaybackStarted);
		} else {
			this.player.getPaused().then((result: boolean) => {
				if (result) {
					this.player.play();
				} else {
					this.player.pause();
				}
			});
			this.trackEvent(VideoTrackingEventNames.VideoPlaybackPausedResumed);
		}
		this.changePlayIcon();
	}

	setTrackingProps() {
		this.trackingProps = {
			eventName: '',
			location: TrackingLocationEnum.ProjectTemplates,
			url: this.url,
		};
	}

	trackEvent(eventName: string) {
		this.trackingProps.eventName = eventName;
		track(this.trackingProps);
	}

	render() {
		const videoPlayerContent = (
			<div>
				<div
					style={{
						minWidth: '300px',
						width: `${this.windowInnerWidth <= this.mobileWindowThreshold ? '100%' : this.width}px`,
					}}
					class={this.getAspectRatio()}
				>
					<div class="video__player" ref={(el) => (this.playerElement = el as HTMLElement)}>
						{this.isHelperVideo && (
							<div
								class="video__btn video__btn-close"
								onClick={() => {
									this.closePlayer();
								}}
							>
								<div class="video__icon-close icon--support" />
							</div>
						)}
						<div
							class="video__btn video__btn-play"
							onClick={() => {
								this.handlePlayer();
							}}
						>
							<span class="video__icon-play" />
						</div>
					</div>
				</div>
			</div>
		);

		const helperVideo = (
			<Host class="video__container" ref={(el) => (this.videoContainer = el as HTMLElement)}>
				{this.showSideBlock && (
					<div class="video__side-block">
						<div class="flex flex--column bubble bubble--dark">
							<p>{this.sideBlockBody}</p>
							<div class="flex flex--column flex--align-center">
								{this.sideBlockStart && (
									<dng-button
										buttonText={this.sideBlockStart}
										buttonClass="btn--primary outset-bottom--s"
										onClick={() => {
											this.handlePlayer(true);
										}}
									/>
								)}
								{this.sideBlockSkip && (
									<span
										class="text--s text--white text--underline cursor--pointer"
										onClick={() => {
											this.closePlayer();
										}}
									>
										{this.sideBlockSkip}
									</span>
								)}
							</div>
						</div>
					</div>
				)}
				{videoPlayerContent}
			</Host>
		);
		return this.isHelperVideo ? helperVideo : videoPlayerContent;
	}
}
